import { useState, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@/redux/reducers'
import {
  ListYeekeePutNumberPagePayload,
  YeekeePutNumberPayload,
} from '@/redux/saga/yeekeeSaga'
import { sagaActions } from '@/redux/sagaActions'
import dayjs, {
  DATE_TIME_SECOND_FORMAT_HUMAN,
  convertToDateTime,
} from '@/utils/date-time-utils'
import { PutNumberEntry } from '@/ui-components/components/BetLotto/hooks/useShootNumberData'
import { toast } from 'react-hot-toast'
import { Round } from '@/redux/reducers/gameReducer'

export interface UseShootNumberV2Return {
  /**
   * Handler for submitting a shoot number
   */
  handleShootNumber: (number: string) => void

  /**
   * Current list of put numbers to display
   */
  currentPutNumbers: PutNumberEntry[]

  /**
   * Total number of pages available
   */
  totalPages: number

  /**
   * Current active page
   */
  currentPage: number

  /**
   * Handler for changing the page
   */
  handlePageChange: (page: number) => void

  /**
   * Whether data is currently loading
   */
  isLoading: boolean

  /**
   * Countdown timer for next submission (if applicable)
   */
  delayCountdown?: number

  /**
   * Countdown date for next submission (if applicable)
   */
  closeAtShootTime?: Date
}

/**
 * Hook for managing shoot number functionality in the Lotto UI
 * @returns Object containing handlers and data for the ShootNumber component
 */
export const useShootNumber = (
  round?: Round,
  isResultConfirmed: boolean = false,
): UseShootNumberV2Return => {
  const { t, i18n } = useTranslation()
  const { gameId, roundId } = useParams()
  const dispatch = useDispatch()

  // State
  const [delayCountdown, setDelayCountdown] = useState<number | undefined>()
  const [currentPage, setCurrentPage] = useState(1)
  const [firstLoadingNumber, setFirstLoadingNumber] = useState(true)
  const pageSize = 20

  // Redux selectors
  const yeekeeData = useSelector((state: RootState) => state.yeekee)
  // We shouldn't expose object from useSelector as it cause re-render as new object is created
  const { loadingPutNumber, loadingFetchNumbers, data, error, response } =
    useMemo(() => {
      return {
        loadingFetchNumbers: yeekeeData.loading.listYeekeePutNumber,
        loadingPutNumber: yeekeeData.loading.putNumberYeekee,
        data: yeekeeData.listYeekeePutNumber,
        error: yeekeeData.error,
        response: yeekeeData.putNumberYeekee,
      }
    }, [yeekeeData])

  useEffect(() => {
    if (!loadingFetchNumbers) {
      setFirstLoadingNumber(false)
    }
  }, [loadingFetchNumbers])

  /**
   * Submit a new put number
   */
  const handleShootNumber = useCallback(
    (putNumber: string) => {
      const payload: YeekeePutNumberPayload = {
        gameId: gameId ? parseInt(gameId) : -1,
        roundId: roundId ? parseInt(roundId) : -1,
        inputNumber: putNumber,
      }

      dispatch({ type: sagaActions.POST_YEEKEE_PUT_NUMBER, payload })

      // Start countdown
      setDelayCountdown(10)
    },
    [gameId, roundId, dispatch],
  )

  /**
   * Fetch the list of put numbers
   */
  const fetchYeekeePutNumbers = useCallback(() => {
    const payload: ListYeekeePutNumberPagePayload = {
      page: currentPage,
      pageSize: pageSize,
      roundId: roundId ?? '',
    }

    dispatch({
      type: sagaActions.FETCH_YEEKEE_LIST_NUMBER,
      payload,
    })
  }, [currentPage, pageSize, roundId, dispatch])

  /**
   * Handle page change
   */
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page)
  }, [])

  /**
   * Mask username for privacy
   */
  const maskUsername = useCallback((username: string) => {
    const firstChar = username.charAt(0)
    const lastChar = username.charAt(username.length - 1)
    const maskedChars = '*'.repeat(username.length - 2)
    return `${firstChar}${maskedChars}${lastChar}`
  }, [])

  // Convert data from Redux to PutNumberEntry format
  const currentPutNumbers: PutNumberEntry[] = (data.data || []).map(
    (item, index) => {
      const rank = (currentPage - 1) * pageSize + index + 1

      return {
        rank,
        number: item.input_number,
        username: !isResultConfirmed
          ? maskUsername(item.player_username)
          : item.player_username,
        dateTime: convertToDateTime(
          item.saved_at,
          DATE_TIME_SECOND_FORMAT_HUMAN,
          i18n.language,
        ),
      }
    },
  )

  // Calculate total pages
  const totalPages = Math.ceil(data.count / pageSize)

  // Handle countdown timer
  useEffect(() => {
    if (delayCountdown) {
      const timer = setTimeout(() => {
        setDelayCountdown(delayCountdown > 1 ? delayCountdown - 1 : undefined)
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [delayCountdown])

  // Handle errors and success messages
  useEffect(() => {
    if (!loadingPutNumber && error) {
      toast.error(error)
      dispatch({ type: sagaActions.CLEAR_GLOBAL_ERROR })
    }

    if (response && response.status === 'OK') {
      toast.success(t('message.successPutNumber'))
      dispatch({ type: sagaActions.CLEAR_YEEKEE_PUT_RESPONSE })
    }
  }, [loadingPutNumber, error, response, t, dispatch])

  // Fetch data on mount and when dependencies change
  useEffect(() => {
    fetchYeekeePutNumbers()

    // Set up polling if results are not yet confirmed
    if (!isResultConfirmed) {
      const intervalId = setInterval(fetchYeekeePutNumbers, 2000)

      return () => {
        clearInterval(intervalId)
      }
    }
  }, [fetchYeekeePutNumbers, currentPage, isResultConfirmed])

  const closeAtShootTime = round
    ? dayjs(round?.close_at).add(
        round?.yeekee_config?.put_number_extend_time ?? 60,
        'second',
      )
    : undefined

  return {
    handleShootNumber,
    currentPutNumbers,
    totalPages,
    currentPage,
    handlePageChange,
    isLoading: firstLoadingNumber || loadingPutNumber,
    delayCountdown,
    closeAtShootTime: closeAtShootTime?.toDate(),
  }
}

export default useShootNumber
